import React, { Suspense, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { scheduleWhenIdle } from '@/shared/lib/scheduleWhenIdle'

/**
 * Hoisted into the entry chunk purely by being imported here, and not on screen
 * until the member does something (IN-46 Phase 4.4): GlobalTaskCreateDialog is a
 * dialog that starts closed. (The announcement composer's DatePicker, the other
 * half of this note, moved to NotificationPanel with the composer — RB-40.)
 *
 * `null` Suspense fallback on purpose: there is nothing sensible to show in the
 * gap, and both are rendered inside layout chrome where a spinner would flash in
 * a corner for one frame.
 */
const GlobalTaskCreateDialog = lazyWithRecovery(
  () => import('@/features/tasks/components/GlobalTaskCreateDialog'),
  'GlobalTaskCreateDialog',
)
import { markFirstPaintDone } from '@/shared/lib/firstPaintSignal'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  LogOut,
  Bell,
  Plus,
} from 'lucide-react'
import { navGroups, allNavItems, isNavRowShown } from '@/shared/components/layout/navGroups'
import { NotificationPanel } from '@/shared/components/layout/NotificationPanel'
import { FullScreenMenu } from '@/shared/components/layout/FullScreenMenu'
import { PhoneBar, PHONE_BAR_OFFSET } from '@/shared/components/layout/PhoneBar'
import { PhoneCreateSheet, PhoneNotificationsSheet } from '@/shared/components/layout/PhoneSheets'
import { runsTheStudio } from '@/shared/access/accessModel'
import { Button } from '@/shared/ui/button'
import { StudioAvatar } from '@/shared/components/StudioAvatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { Separator } from '@/shared/ui/separator'
import { useAuthStore } from '@/shared/store/authStore'
import { useStudioSettings } from '@/shared/hooks/useStudioSettings'
import { formatBytes } from '@/shared/lib/formatters'
import { useThemeStore } from '@/shared/store/themeStore'
import { apiClient } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import { bookingAdminApi } from '@/features/booking/api'
import { automationApi } from '@/features/automations/api'
import { StudioSwitcher } from '@/features/auth/components/StudioSwitcher'
import { ThemeToggle } from '@/shared/components/ThemeToggle'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useTranslation } from 'react-i18next'
import { useUiLocaleSync } from '@/i18n/useUiLocaleSync'
import { useServerRenderedTextRefresh } from '@/i18n/useServerRenderedTextRefresh'
import { usePushSubscriptionSync } from '@/shared/hooks/usePushSubscriptionSync'
import { useAppUpdater } from '@/shared/hooks/useAppUpdater'
import { useUserPreferences } from '@/shared/hooks/useUserPreferences'
import { useScrollRestoration } from '@/shared/hooks/useScrollRestoration'
import { useOverlayRouteReset } from '@/shared/hooks/useOverlayRouteReset'
import { usePendingPushNavigation } from '@/shared/hooks/usePendingPushNavigation'
import { invalidateNotifications } from '@/shared/lib/notificationsInvalidation'
import { connectChat } from '@/shared/lib/chatWebSocket'
import { lazyWithRecovery } from '@/lib/lazyWithRecovery'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useWritesLocked, LIFECYCLE_BANNER_ID } from '@/shared/hooks/use-lifecycle'
/*
 * Static, deliberately. This was a lazyWithRecovery boundary, which rolldown
 * reported as INEFFECTIVE_DYNAMIC_IMPORT: seventeen feature modules import
 * date-picker statically, so it is hoisted into the entry chunk regardless and
 * the dynamic import could never move it. Measured — routing it through a
 * re-export module to strip the eager reference changed the entry chunk by 61
 * bytes, i.e. nothing. The lazy() bought a Suspense fallback and stale-chunk
 * recovery for a module that is already in the bundle by the time this renders.
 * Getting react-day-picker out of the entry is a manualChunks question; it is
 * in docs/backlog.md, not solvable from this import site.
 */

/** Decode the roles claim from a JWT payload (base64url JSON). Falls back to []. */
function rolesFromJwt(token: string | null): string[] {
  if (!token) return []
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return Array.isArray(payload.roles) ? payload.roles : []
  } catch {
    return []
  }
}

// The notification timestamps go through the SAME formatter as the activity timeline, the
// shoots page and the gallery list — see shared/lib/formatters.ts. This file's copy was the
// only one of the four that was actually translated, via four `layout:time.*` keys; those
// keys are deleted with this change, because CLDR ships the same four phrases in every
// locale and gets the plurals right in languages our hand-written pairs could not. It also
// picks up the seven-day cutoff the other three had: a notification older than a week now
// reads "15. Okt. 2026" instead of counting up forever to "vor 400 T.".




// memo matters here: the 2026-08-20 D1 profiling showed NavLinks re-rendering
// on literally every commit in the app (45/45) because AppLayout re-renders on
// each of its ~8 queries/polls and every navigation, dragging the whole
// ~400-fiber shell along. With memoized props (navItems via useMemo,
// navigateFromDrawer via useCallback) the nav now only re-renders for its own
// badge queries and locale changes; NavLink active-state still updates through
// router context.
const NavLinks = memo(function NavLinks(
  { onNavigate, visibleItems, variant = 'list' }:
  {
    /**
     * `list` is the desktop sidebar. `grid` is the phone's full-screen menu (RB-40 D84): the
     * same rows, filter and badges, laid out as two columns of 44 px rows so every first-level
     * row fits on an 844 px phone without scrolling — the drawer this replaced held them in one
     * column of 48 px rows and always hid Finance, Team, Settings and Help below a scroll cue.
     */
    variant?: 'list' | 'grid'
    /**
     * Phone menu only. Receives the click so it can preventDefault and take
     * over the navigation — see navigateFromDrawer in AppLayout for why the
     * timing of the navigation matters. Desktop passes nothing and NavLink
     * navigates normally.
     */
    onNavigate?: (e: React.MouseEvent, to: string) => void
    visibleItems: typeof allNavItems
  },
) {
  const { t } = useTranslation()
  // Build a set of visible destinations for quick lookup
  const visibleTos = new Set(visibleItems.map(i => i.to))

  // Unread chat badge
  const { data: chatChannels } = useQuery<{ unreadCount: number }[]>({
    queryKey: queryKeys.chat.channels,
    queryFn: async () => { const { data } = await apiClient.get('/chat/channels'); return data },
    refetchInterval: 30_000,
    staleTime: 10_000,
  })
  const totalChatUnread = (chatChannels ?? []).reduce((s, c) => s + c.unreadCount, 0)

  // Inquiries badge — pending booking requests + unconfirmed form/ingest inquiries.
  // Only OWNER/MANAGER/ASSISTANT see the nav entry, so gate both queries on its
  // visibility to avoid a 403 for other roles. Both caches are shared with the
  // dashboard banner so the counts are fetched once.
  const inquiriesVisible = visibleTos.has('/inquiries')
  const { data: pendingBookings } = useQuery<number>({
    queryKey: queryKeys.booking.pendingCount,
    queryFn: () => bookingAdminApi.pendingCount(),
    enabled: inquiriesVisible,
    refetchInterval: 30_000,
    staleTime: 10_000,
  })
  const { data: formInquiryCount } = useQuery<number>({
    queryKey: queryKeys.booking.formInquiryCount,
    queryFn: async () => {
      const { data } = await apiClient.get('/booking/requests/form-inquiry-count')
      return data.count as number
    },
    enabled: inquiriesVisible,
    refetchInterval: 30_000,
    staleTime: 10_000,
  })
  const inquiriesBadgeCount = (pendingBookings ?? 0) + (formInquiryCount ?? 0)

  // RB-MT-01 F1 — keep the Inquiries lists as fresh as this badge.
  // The two counts above poll every 30s, but the lists behind /inquiries had no
  // polling and no external invalidation, so a lead that arrived while the page was
  // open bumped the badge and never showed up until a hard refresh. Piggyback on the
  // poll that already runs: whenever a count *changes*, mark the inquiry lists stale.
  // No new traffic on the steady state, and no per-page polling loop.
  //
  // The two count queries are deliberately excluded from the invalidation — they are
  // what just delivered the new value, so invalidating them would refetch them again
  // on every change.
  const navQc = useQueryClient()
  const prevInquiryCounts = useRef<{ pending: number; form: number } | null>(null)
  useEffect(() => {
    if (!inquiriesVisible || pendingBookings === undefined || formInquiryCount === undefined) return
    const prev = prevInquiryCounts.current
    prevInquiryCounts.current = { pending: pendingBookings, form: formInquiryCount }
    // First delivery carries no information about staleness — the lists loaded with it.
    if (prev === null || (prev.pending === pendingBookings && prev.form === formInquiryCount)) return
    navQc.invalidateQueries({
      predicate: q => {
        if (q.queryKey[0] !== 'booking') return false
        const last = q.queryKey[q.queryKey.length - 1]
        return last !== 'count' && last !== 'form-inquiry-count'
      },
    })
    navQc.invalidateQueries({ queryKey: queryKeys.projects.inquiryInProgress })
  }, [pendingBookings, formInquiryCount, inquiriesVisible, navQc])

  // Pending automation-approvals badge — only OWNER/MANAGER see the Automations nav
  // entry (matches the /automation/pending endpoint RBAC), so gate the query on its
  // visibility to avoid a 403 for other roles. Reuses the approval-queue list; pending
  // queues are small, so its length is the badge count.
  const automationsVisible = visibleTos.has('/automations')
  const { data: pendingApprovals } = useQuery({
    queryKey: queryKeys.automations.pending,
    queryFn: automationApi.listPending,
    enabled: automationsVisible,
    refetchInterval: 30_000,
    staleTime: 10_000,
  })
  const pendingApprovalCount = pendingApprovals?.length ?? 0

  // Inbox NEW-email badge — only OWNER/MANAGER/ASSISTANT/CONTRIBUTOR see the Inbox
  // nav entry (matches the /inbox RBAC), so gate the count query on its visibility
  // to avoid a 403 for other roles.
  const inboxVisible = visibleTos.has('/emails')
  const { data: inboxUnread } = useQuery<{ count: number }>({
    queryKey: queryKeys.inbox.unreadCount,
    queryFn: async () => { const { data } = await apiClient.get('/inbox/unread-count'); return data },
    enabled: inboxVisible,
    refetchInterval: 30_000,
    staleTime: 10_000,
  })
  const inboxUnreadCount = inboxUnread?.count ?? 0

  // `overscroll-contain` on the <nav> stops wheel events from bubbling
  // to the page once the sidebar reaches its top/bottom — without it,
  // scrolling inside the sidebar drags the main content along, which
  // feels broken on desktop.
  const grid = variant === 'grid'
  return (
    <nav
      // The pressed state below replaces iOS's grey tap flash, which would otherwise
      // double up with it and tint the whole row. In the grid the scroll is a safety net
      // for a long translation or a large text setting, not the layout.
      className={grid
        ? 'flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain px-3 pb-3 pt-2 [-webkit-tap-highlight-color:transparent]'
        : 'flex flex-1 flex-col p-2 overflow-y-auto overscroll-contain min-h-0 [-webkit-tap-highlight-color:transparent]'}
    >
      {navGroups.map((group, gi) => {
        const groupItems = group.items.filter(i => visibleTos.has(i.to))
        if (groupItems.length === 0) return null
        return (
          <div key={group.groupKey}>
            {gi > 0 && !grid && <Separator className="my-1.5" />}
            <p className={grid
              ? 'px-2 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 select-none'
              : 'px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60 select-none'}>
              {t(group.groupKey)}
            </p>
            <div className={grid ? 'grid grid-cols-2 gap-x-1.5 gap-y-1' : 'flex flex-col gap-0.5'}>
              {groupItems.map(({ to, labelKey, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={onNavigate ? e => onNavigate(e, to) : undefined}
                  className={({ isActive }) =>
                    // Grid rows are the phone menu's: 44 px tall (RB-31), two to a line, with a
                    // pressed state so a tap visibly lands somewhere. Desktop keeps its dense list.
                    `${grid
                      ? 'flex min-h-11 min-w-0 items-center gap-2.5 rounded-md px-2.5 text-[15px]'
                      : 'flex items-center gap-3 rounded-md px-3 py-1.5 text-sm'} transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground active:brightness-90'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground active:bg-accent active:text-accent-foreground'
                    }`
                  }
                >
                  {/* IN-18. Not a missing dark token — every nav row renders the same
                      `text-muted-foreground`, and Andras's screenshot shows the label and
                      the purple badge on the Automations row both perfectly legible beside
                      a glyph that had all but disappeared. It is ink: at size 14 lucide's
                      default stroke renders at 14/24 × 2 = 1.17 CSS px, and a sub-pixel
                      stroke anti-aliased against a dark ground loses most of its contrast.
                      Boxy glyphs (Inbox, FolderOpen, CheckSquare) survive that because their
                      edges are axis-aligned and snap to the pixel grid; `Zap` is a single
                      thin diagonal outline with the least ink on the surface and no straight
                      edge to snap, so it went first.

                      So the fix is the class, not the instance: 16px at stroke 2.25 renders
                      at 1.5 px for EVERY row. Swapping Zap for a denser glyph would have
                      fixed one row and left the next-thinnest to be reported next. */}
                  <Icon size={16} strokeWidth={2.25} className={grid ? 'h-[18px] w-[18px] shrink-0' : undefined} />
                  <span className={grid ? 'min-w-0 flex-1 truncate' : 'flex-1'}>{t(labelKey)}</span>
                  {to === '/chat' && totalChatUnread > 0 && (
                    <span className="rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 text-[11px] font-bold leading-none">
                      {totalChatUnread > 9 ? '9+' : totalChatUnread}
                    </span>
                  )}
                  {to === '/inquiries' && inquiriesBadgeCount > 0 && (
                    <span className="rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 text-[11px] font-bold leading-none">
                      {inquiriesBadgeCount > 9 ? '9+' : inquiriesBadgeCount}
                    </span>
                  )}
                  {to === '/automations' && pendingApprovalCount > 0 && (
                    <span className="rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 text-[11px] font-bold leading-none">
                      {pendingApprovalCount > 9 ? '9+' : pendingApprovalCount}
                    </span>
                  )}
                  {to === '/emails' && inboxUnreadCount > 0 && (
                    <span className="rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 text-[11px] font-bold leading-none">
                      {inboxUnreadCount > 9 ? '9+' : inboxUnreadCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        )
      })}
    </nav>
  )
})

const GlobalUploadProgress = lazyWithRecovery(
  () => import('@/shared/components/GlobalUploadProgress'),
  'GlobalUploadProgress',
)
const PWAOnboardingBanner = lazyWithRecovery(
  () => import('@/shared/components/PWAOnboardingBanner'),
  'PWAOnboardingBanner',
)
const LifecycleBanner = lazyWithRecovery(
  () => import('@/shared/components/LifecycleBanner'),
  'LifecycleBanner',
)

/**
 * The avatar-and-name menu at the foot of the sidebar / in the mobile top bar.
 *
 * A component rather than a JSX constant because AppLayout renders it in three
 * places (desktop sidebar, mobile drawer, mobile top bar) and the open state
 * has to be per-instance: on a phone the drawer's copy and the top bar's copy
 * are both mounted while the drawer is open, and one shared `open` would pop
 * two portalled dropdowns at once. Each instance also owns whether the studio
 * switcher inside it may fetch.
 *
 * memo for the same D1 reason as NavLinks: 90 renders per short profiling
 * session, all parent churn. Props are two primitives and a useCallback.
 */
const UserMenu = memo(function UserMenu({
  memberId,
  memberName,
  onLogout,
}: {
  memberId?: number
  memberName?: string
  onLogout: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {/* w-full min-w-0 on the button and min-w-0 on the label: the wrapper below is
            already flex-1 min-w-0, but an inline-flex button shrink-to-fits to its
            content and spills past it, which is how "Andrea Sommer" ended up under the
            notification badge instead of ellipsing. */}
        <Button variant="ghost" className="w-full min-w-0 justify-start gap-2 px-2 max-md:h-12 active:bg-accent [-webkit-tap-highlight-color:transparent]">
          <StudioAvatar seed={memberId ?? 0} name={memberName} size={28} />
          <span className="min-w-0 truncate text-sm">{memberName ?? t('layout:userMenu.fallbackName')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {/* Renders nothing unless this identity belongs to ≥2 studios, and only
            fetches while the menu is open — a single-studio member pays for
            nothing here. */}
        <StudioSwitcher enabled={open} onSwitching={() => setOpen(false)} />
        <DropdownMenuItem onClick={onLogout}>
          <LogOut size={14} className="mr-2" /> {t('layout:userMenu.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
})

export function AppLayout() {
  // Sync i18next with the logged-in user's saved locale preference.
  // Runs once here since AppLayout wraps the entire authenticated tree;
  // unauthenticated pages (login, public accept) use browser detection
  // only.
  useUiLocaleSync()

  // IN-37: i18next flips the chrome instantly and client-side, but the attention items are
  // server-rendered and sit in a TanStack cache that a language change says nothing to. This
  // marks them stale on every `changeLanguage` — including the one useUiLocaleSync itself
  // fires — so the page never shows two languages at once.
  useServerRenderedTextRefresh()

  // Silently re-create a push subscription the browser dropped (iOS can evict
  // a PWA's sub across OS updates). No-op unless permission is already granted
  // and the sub is gone — never prompts. See usePushSubscriptionSync.
  usePushSubscriptionSync()

  // Poll /version.json and silently reload when a new build is deployed.
  // No-op in DEV. Defers reload when a dialog is open or the user is typing.
  useAppUpdater()

  const { t } = useTranslation()
  const { teamMember, accessToken, clearAuth, updateMember } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  const [sheetOpen, setSheetOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  // The phone bar's two sheets. The notifications sheet has its own flag, not the desktop
  // dropdown's: the sidebar is only hidden by CSS below md, and one flag would mount the
  // panel twice.
  const [phoneNotifOpen, setPhoneNotifOpen] = useState(false)
  const [createSheetOpen, setCreateSheetOpen] = useState(false)

  /*
   * R9. The nav drawer must not be live across a route change, and no overlay
   * may leave document.body inert behind it. See the hook for why the two
   * belong together — the drawer the user sees after a swipe-back is iOS's
   * cached screenshot, but the unresponsiveness is a real Radix leak.
   */
  useOverlayRouteReset(() => { setSheetOpen(false); setPhoneNotifOpen(false); setCreateSheetOpen(false) })

  /*
   * R9 — navigating OUT of the mobile nav drawer.
   *
   * The on-device log finally showed what was happening. Neither of the earlier
   * theories survived it: after the swipe back the drawer is `closed` in React and
   * <body> is clean, so nothing is actually open and nothing is inert. What Andras
   * sees is iOS's cached screenshot of the previous history entry — and the reason
   * that screenshot contains an open drawer is the 20ms between these two lines:
   *
   *     15.32s drawer=closed
   *     15.34s ROUTE /chat -> /projects
   *
   * SheetContent exits over `data-[state=closed]:duration-300`. Committing the
   * navigation 20ms in captures the frame with the drawer still ~93% on screen, and
   * iOS then replays exactly that on the way back — which is why it looks open, why
   * tapping it does nothing (it is a picture; taps fall through to the page beneath),
   * and why it vanishes on its own once the app finally paints.
   *
   * So: tear the drawer down with NO exit animation, let a frame actually paint
   * without it, and only then navigate. The snapshot iOS keeps is then drawer-free.
   */

  const navigateFromDrawer = useCallback((e: React.MouseEvent, to: string) => {
    // Leave modified clicks and anything already handled alone — a ctrl/cmd-click
    // should still open a new tab.
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    e.preventDefault()

    setSheetOpen(false)

    /*
     * Two frames, not one. A rAF callback runs BEFORE the paint of its own frame,
     * so navigating from the first one would still commit before the drawer-free
     * frame reaches the screen. The second callback runs after that paint, which
     * is the guarantee this whole thing rests on. Costs ~32ms.
     *
     * The timeout is not belt-and-braces, it is required. rAF does not fire in a
     * backgrounded or non-painting tab, and we have taken over the navigation with
     * preventDefault — so without a fallback a tap in that state would go nowhere
     * at all and the app would look broken. Whichever path runs first wins; 120ms
     * is far longer than two frames and far shorter than a user notices.
     */
    let navigated = false
    const go = () => {
      if (navigated) return
      navigated = true
      navigate(to)
    }
    requestAnimationFrame(() => requestAnimationFrame(go))
    window.setTimeout(go, 120)
  }, [navigate])

  // ── Sidebar collapsed (desktop) ──────────────────────────────────────────
  // localStorage is the first-paint fallback; server preference is the
  // cross-device source of truth and is applied once after prefs load.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sf_sidebar_collapsed') === 'true'
  )
  const prefsSyncedRef = useRef(false)
  const userPrefs = useUserPreferences()
  useEffect(() => {
    if (!prefsSyncedRef.current && userPrefs !== undefined) {
      prefsSyncedRef.current = true
      if (userPrefs.sidebarCollapsed != null) {
        localStorage.setItem('sf_sidebar_collapsed', String(userPrefs.sidebarCollapsed))
        setSidebarCollapsed(userPrefs.sidebarCollapsed)
      }
    }
  }, [userPrefs])

  // Sync theme from server preferences
  const { syncFromServer } = useThemeStore()
  useEffect(() => {
    if (userPrefs?.theme === 'light' || userPrefs?.theme === 'dark' || userPrefs?.theme === 'system') {
      syncFromServer(userPrefs.theme)
    }
  }, [userPrefs?.theme, syncFromServer])
  const [notifOpen, setNotifOpen] = useState(false)
  const desktopBellRef = useRef<HTMLDivElement>(null)
  const notifDropdownRef = useRef<HTMLDivElement>(null)
  const mainScrollRef = useRef<HTMLElement>(null)

  // Scroll-to-top on route change AND restore the <main> offset on Back — the
  // browser only ever restores the document scroller, which this app is not
  // using. See the hook for the full reasoning.
  useScrollRestoration(mainScrollRef)

  // teamMember.roles is populated on fresh login. For sessions that pre-date the
  // MemberSummary addition to LoginResponse, fall back to decoding the JWT directly.
  const storedRoles = teamMember?.roles ?? []
  const userRoles = storedRoles.length > 0 ? storedRoles : rolesFromJwt(accessToken)
  /*
   * Owner or Admin — the two levels that run the studio (ADR-036 axis A), read through
   * `shared/access/accessModel` so the next rename cannot leave a literal behind.
   *
   * This spelled `['ROLE_OWNER', 'ROLE_MANAGER']` until now, and the second half was a
   * name no membership carries any more: the predicate silently collapsed to "is Owner",
   * which cost an ADMIN all seven things it gates. Measured live before the fix (org
   * `applayout2`, run `fix-applayout-gates`): Owner storage-query=1/announce=yes, Admin
   * 0/no. Nothing 403s and nothing logs — the controls are simply never there — which is
   * why it survived from the access rewrite until a per-level sign-in went looking.
   *
   * One predicate still fits all seven sites, and OWNER+ADMIN is the right set for each:
   *   • the announce button + the announcement dismiss ✕ — `GlobalNotificationController`
   *     POST and DELETE are `hasAnyRole('OWNER','ADMIN')`, an exact match;
   *   • the label create/delete controls inside the "+" dialog's label manager —
   *     `TaskLabelController` write methods are `hasAnyRole('OWNER','ADMIN')`, likewise;
   *   • the storage indicator. `GET /settings/storage` is only `isAuthenticated()`, so
   *     this one gate IS stricter than its endpoint — deliberately. The indicator is a
   *     link to /settings offering to do something about the quota, and buying headroom
   *     (`POST /settings/storage/extend`) is `hasRole('OWNER')`. BOOKKEEPER is out on the
   *     same reasoning read the other way: they upload nothing, so the studio's disk is
   *     not a number they can act on or are asked to.
   */
  const isOwnerOrAdmin = runsTheStudio(userRoles)
  // useMemo is load-bearing, not an optimisation nicety: NavLinks is memo'd,
  // and a fresh filter() array every render would defeat that memo entirely
  // (the D1 every-commit shell re-render). Deps cover both role sources.
  //
  // `navGroups.ts` is the whole gate. It used to be followed by a second clause hiding
  // /my-projects, /my-galleries and /my-tasks from "full management access" — dead code
  // since the access rewrite, because the table already gives those three rows
  // `roles: [AUTH.CREW]` and nobody holds two levels. It could only ever have fired for a
  // Crew member, and it excluded Crew. Removed rather than renamed: keeping it would put
  // a second, contradictory opinion about those rows next to the one that decides them.
  //
  // Filtered through `isNavRowShown` (reachable AND the right scope form of a doubled row,
  // RB-28 item 9) rather than a bare `hasAnyAuthority(roles)`: the
  // table grew a NEGATIVE half on 2026-09-08 (`deniedRoles`, for `/clients` and the
  // `SCOPE_ASSIGNED_ONLY` switch its endpoint refuses), and a second reading of the
  // table here would have honoured the positive list and silently dropped the denial.
  const navItems = useMemo(() => allNavItems.filter(({ to }) => isNavRowShown(to, userRoles)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [teamMember?.roles, accessToken])

  // ── Studio name for header ────────────────────────────────────────────────
  // Single source of truth: settings API. No stale fallback — show nothing
  // while loading (~100ms on hard refresh) to avoid flashing a wrong name.
  // Also syncs orgName in auth store so localStorage stays current.
  const { data: studioSettings } = useStudioSettings()
  useEffect(() => {
    const name = studioSettings?.studioName as string | undefined
    if (name && name !== teamMember?.orgName) {
      updateMember({ orgName: name })
    }
  }, [studioSettings?.studioName])
  // teamMember.orgName stays the fallback. GET /settings is Owner/Admin/Finance, so a
  // Crew member used to 403 here and the header went BLANK for exactly the roles that
  // most need it (and left the console error of F70). useStudioSettings now reads the
  // presentation slice for those roles instead, so the name arrives — but the fallback
  // stays, because under ADR-033 one login can belong to several studios and "which
  // studio am I in" is a question the chrome has to answer before any request resolves.
  const headerName =
    (studioSettings?.displayName as string | undefined)
    || (studioSettings?.studioName as string | undefined)
    || teamMember?.orgName || ''

  // ── Storage usage (Owner/Admin — see `isOwnerOrAdmin` above) ────────────
  const { data: storageData } = useQuery<{ usedBytes: number; limitBytes: number; usagePercent: number }>({
    queryKey: queryKeys.settings.storageUsage,
    queryFn: async () => (await apiClient.get('/settings/storage')).data,
    staleTime: 5 * 60 * 1000,
    enabled: isOwnerOrAdmin,
  })

  // ── Service worker push messaging ──────────────────────────────────────────
  // The service worker posts two message types:
  //   • NAVIGATE — the user tapped a push while the app is open. React Router's
  //     navigate() only understands in-app paths, so coerce any absolute
  //     same-origin URL down to pathname+search+hash (this was the mobile
  //     deep-link 404: an absolute URL made navigate() land on the 404 page).
  //   • PUSH_RECEIVED — a push just arrived. Live-refresh the notification bell
  //     (list + red count), the tasks board, and chat badges so nothing lags
  //     behind the OS notification or needs a manual reload.
  useEffect(() => {
    const toAppPath = (url: string): string => {
      try {
        const u = new URL(url, window.location.origin)
        if (u.origin === window.location.origin) return u.pathname + u.search + u.hash
      } catch { /* not a URL — treat as a path below */ }
      return url
    }
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'NAVIGATE' && typeof event.data.url === 'string') {
        navigate(toAppPath(event.data.url))
      } else if (event.data?.type === 'PUSH_RECEIVED') {
        invalidateNotifications(qc)
        qc.invalidateQueries({ queryKey: queryKeys.chat.channels })
        qc.invalidateQueries({ queryKey: queryKeys.tasks.all })
      }
    }
    navigator.serviceWorker?.addEventListener('message', handler)
    return () => navigator.serviceWorker?.removeEventListener('message', handler)
  }, [navigate, qc])

  // The NAVIGATE message above is the fast path and it is not reliable on its own:
  // a frozen PWA window never receives it, and an iOS cold launch never had a
  // client to send it to. The worker also parks the destination where the page can
  // read it, which is what this consumes — on mount (cold start) and on resume.
  // IN-12: without it the tap opened the app and left the reader where they were.
  usePendingPushNavigation(navigate)

  // ── Notification queries ──────────────────────────────────────────────────
  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: async () => { const { data } = await apiClient.get('/notifications/unread-count'); return data },
    refetchInterval: 30_000,
    staleTime: 0,
  })
  const unreadCount = unreadData?.count ?? 0

  // Close popover on outside click (check both desktop + mobile bell containers,
  // the dropdown itself, and any Radix portal content like the DatePicker calendar)
  useEffect(() => {
    if (!notifOpen) return
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (
        desktopBellRef.current?.contains(t) ||
        notifDropdownRef.current?.contains(t) ||
        (t as Element).closest?.('[data-radix-popper-content-wrapper]') ||
        // The panel's own confirm ("Delete all") is a portalled alert dialog; a click on it, or
        // on its overlay, must not close the panel and unmount the dialog mid-question.
        document.querySelector('[role="alertdialog"]')
      ) return
      setNotifOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [notifOpen])

  // useCallback so the memo'd UserMenu (three instances) doesn't re-render on
  // every AppLayout render just because this handler got a new identity.
  const handleLogout = useCallback(async () => {
    // The refresh-token cookie is cleared server-side by the logout endpoint
    // (Set-Cookie with maxAge=0). Best-effort — if the network is down we
    // still wipe the in-memory access token + member and bounce.
    try { await apiClient.post('/auth/logout') } catch { /* best-effort */ }
    clearAuth()
    navigate('/login')
  }, [clearAuth, navigate])

  // ── Chat ──────────────────────────────────────────────────────────────────
  // The phone bar's Chat slot shows as current here. (The floating chat bubble, its
  // scroll-awareness and its composer yield went with RB-40 D84: the bar sits in the layout,
  // so nothing has to get out of anything's way.)
  const isOnChatPage = location.pathname.startsWith('/chat')
  // Warm the cold path while the user reads any page — but AFTER the browser is
  // idle, not during mount. Both of these are speculative work for a page the
  // member may never open, and both used to run on every page's mount: opening a
  // STOMP socket (a connection plus a handshake, competing with the dashboard's
  // own ~22 requests) and pulling the 21 KB Chat chunk. Warming a cold path at the
  // exact moment the hot path is loading is the wrong trade (IN-46 Phase 4.2).
  //
  // connectChat is idempotent; ChatPage's own connect effect still covers
  // direct-URL entry and e2e tests that skip AppLayout, so a member who navigates
  // to /chat before the idle callback fires loses nothing but the head start.
  // Raise the "hot path is done" signal the moment the page's own queries have
  // all settled at least once. Hosted here rather than in DashboardPage so it
  // covers whichever page the member's home preference actually lands on.
  //
  // The `sawFetching` guard is load-bearing: the count is 0 for the first render
  // or two, before any query has started, so marking on a bare `=== 0` would fire
  // immediately and defer nothing.
  //
  // WHY AN IMPERATIVE SUBSCRIPTION AND NOT `useIsFetching()` — this was the single
  // cause of ~13 lines of React's "Cannot update a component (`AppLayout`) while
  // rendering a different component (`ProjectChecklist` / `TasksTab` / …)" on every
  // project page, and the same line from `RequestCard` on /inquiries.
  // `useIsFetching()` subscribes AppLayout to the WHOLE query cache through
  // `useSyncExternalStore`. A child mounting a query key the cache has not seen runs
  // `new QueryObserver` -> `QueryCache.build` -> `add` -> `notify` DURING that child's
  // render, and `notifyManager.batch` flushes synchronously — so AppLayout is told to
  // re-render from inside someone else's render. Nothing in AppLayout's output depends
  // on the count; it only wants an edge, so it takes it off-render. As a bonus AppLayout
  // (the whole app chrome) no longer re-renders on every query transition in the app.
  const fetchWatchQc = useQueryClient()
  const sawFetching = useRef(false)
  useEffect(() => {
    const check = () => {
      if (fetchWatchQc.isFetching() > 0) { sawFetching.current = true; return }
      if (sawFetching.current) markFirstPaintDone()
    }
    check()
    return fetchWatchQc.getQueryCache().subscribe(check)
  }, [fetchWatchQc])

  useEffect(() => {
    if (!accessToken) return
    const warm = () => connectChat(accessToken)
    const cancel = scheduleWhenIdle(warm)
    return cancel
  }, [accessToken])
  // Chat is one tap from every page (the phone bar's slot, the sidebar's row), so its chunk is
  // warmed once the browser is idle.
  useEffect(() => {
    const cancel = scheduleWhenIdle(() => {
      import('@/features/chat/pages/ChatPage').catch(() => {})
    })
    return cancel
  }, [])
  // The desktop sidebar's "New Task": eligible roles (can see a task board).
  //
  // `/my-tasks` counts as of IN-15. It used to read `/tasks` only, with the comment
  // "so /my-tasks-only roles never get it" — true while `/my-tasks` was a read-only card
  // list, and wrong the moment it became the full board. Crew lost the `/tasks` row in
  // that change; taking the one-tap create with it would have been a capability removed
  // by accident of how this line happened to be written, not by a decision. `POST /tasks`
  // admits any authenticated member, and it always has.
  const taskFabVisible = navItems.some(i => i.to === '/tasks' || i.to === '/my-tasks') 
  /*
   * IN-107 — the quick-create is a SECOND door to the same dialog /tasks already
   * refuses under the lifecycle lock, so it has to refuse it in the same way.
   * Leaving it live while the Tasks page's own "New Task" is disabled is exactly the
   * two-doors-to-one-state trap: one tap works, the other does not, for one state.
   *
   * Both controls are icon-only with nowhere to hang a sentence, which is why the
   * earlier sweep left them out. `aria-describedby` points at the lifecycle banner
   * the shell is already rendering above them — the reason IS on screen, in the same
   * chrome, and it is the one place it can be without inventing a second wording for
   * a message that must not drift from the banner's.
   */
  const writesLocked = useWritesLocked()
  // The dialog is hosted here rather than reached by navigating to
  // /tasks?new=1: see GlobalTaskCreateDialog for why that navigation was wrong.
  const [taskCreateOpen, setTaskCreateOpen] = useState(false)
  const { data: fabChatChannels } = useQuery<{ id: number; unreadCount: number }[]>({
    queryKey: queryKeys.chat.channels,
    queryFn: async () => { const { data } = await apiClient.get('/chat/channels'); return data },
    refetchInterval: 30_000,
    staleTime: 10_000,
    enabled: !!accessToken,
  })
  const totalUnread = (fabChatChannels ?? []).reduce((s, c) => s + c.unreadCount, 0)
  const handleChatClick = () => {
    // Validate the remembered channel still exists for the current user —
    // a stale sessionStorage value (channel deleted, user switched org,
    // tab resurrected after a long gap) was routing us to /chat/<id>
    // for a channel no longer in the sidebar, rendering an empty thread
    // pane with no way back.
    const lastChannel = sessionStorage.getItem('lastChatChannel')
    const lastIsValid = !!lastChannel
      && (fabChatChannels ?? []).some(c => String(c.id) === lastChannel)
    if (!lastIsValid && lastChannel) {
      // Crumb pointed at something that no longer exists — clear it.
      sessionStorage.removeItem('lastChatChannel')
    }
    navigate(lastIsValid ? `/chat/${lastChannel}` : '/chat')
  }

  // ── Notification bell ─────────────────────────────────────────────────────
  // The panel (list, announcements, mark-read, delete-all) is NotificationPanel, shared with the
  // phone bar's sheet (RB-40 part B). Desktop and the phone's dropdown host it here.
  const notifDropdownInner = (
    <NotificationPanel
      open={notifOpen}
      unreadCount={unreadCount}
      isOwnerOrAdmin={isOwnerOrAdmin}
      onNavigate={() => setNotifOpen(false)}
    />
  )

  // Desktop bell — at the BOTTOM of the sidebar, so dropdown opens UPWARD
  const desktopBell = (
    <div ref={desktopBellRef} className="relative">
      <button
        onClick={() => setNotifOpen(v => !v)}
        className="relative inline-flex items-center justify-center p-2 rounded-md hover:bg-accent max-md:h-11 max-md:w-11"
        aria-label={t('layout:notifications.ariaLabel')}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[11px] font-bold text-white leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {notifOpen && (
        <div ref={notifDropdownRef} className="absolute left-0 bottom-full mb-1 z-50 w-80 rounded-lg border bg-card shadow-lg overflow-hidden flex flex-col max-h-[min(480px,calc(100vh-80px))]">
          {notifDropdownInner}
        </div>
      )}
    </div>
  )


  const userMenu = (
    <UserMenu
      memberId={teamMember?.id}
      memberName={teamMember?.name}
      onLogout={handleLogout}
    />
  )

  /**
   * Desktop's one-tap New Task (the phone's is "+" in the phone bar, RB-40 D84).
   *
   * Lives in the sidebar because that is where desktop chrome belongs — a button
   * floating over the page covered whatever sat in the bottom-right corner.
   * Chat needed no equivalent: the sidebar's Chat entry already carries the same
   * unread badge, so the bubble was duplicating a control that was always there.
   *
   * Rendered inside <aside>, which is `hidden md:flex` — so it is desktop-only by
   * containment, no viewport check needed.
   */
  const newTaskSidebarButton = taskFabVisible && (
    <Button
      variant="ghost" size="icon"
      className="h-8 w-8 shrink-0 disabled:pointer-events-none disabled:opacity-40"
      title={t('layout:taskFab.newTask')}
      aria-label={t('layout:taskFab.newTask')}
      disabled={writesLocked}
      aria-disabled={writesLocked || undefined}
      aria-describedby={writesLocked ? LIFECYCLE_BANNER_ID : undefined}
      onClick={() => setTaskCreateOpen(true)}
    >
      <Plus size={16} />
    </Button>
  )

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      {/* First tab stop of the shell (F43): without it a keyboard user tabs through the logo and
          every sidebar row on every page before reaching the content. Hidden until focused. The
          click handler moves focus itself because a plain #hash jump would only scroll, and
          would fight the router. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[200] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2.5 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        onClick={e => {
          e.preventDefault()
          mainScrollRef.current?.focus()
        }}
      >
        {t('layout:skipLink.label')}
      </a>
      {/* ── Desktop sidebar (md and above) ── */}
      {/* w-56, not w-48. At 192px the header row spent 100px on padding plus the
          collapse and theme buttons, leaving ~92px for the studio name — so anything
          longer than about nine characters ellipsed ("Demo Stu..."), and the member
          name at the foot ran under the notification bell. The sidebar is fixed-width
          at every window size, so no amount of screen real estate fixed it. 224px
          clears both with room for a realistic studio name. */}
      <aside className={`hidden md:flex flex-col border-r bg-card pt-safe transition-all duration-200 ${sidebarCollapsed ? 'w-12' : 'w-56'}`}>
        {sidebarCollapsed ? (
          /* Collapsed: icon-only strip */
          <div className="flex flex-col items-center gap-2 py-3 flex-1">
            <Button
              variant="ghost" size="icon"
              className="h-8 w-8 shrink-0"
              title={t('layout:sidebar.expand')}
              onClick={() => {
                const next = false
                setSidebarCollapsed(next)
                localStorage.setItem('sf_sidebar_collapsed', String(next))
                qc.getQueryData(queryKeys.settings.preferences) &&
                  apiClient.put('/team-members/me/preferences', { sidebarState: 'expanded' }).catch(() => {})
              }}
            >
              <PanelLeftOpen size={16} />
            </Button>
            {newTaskSidebarButton}
          </div>
        ) : (
          /* Expanded: full sidebar */
          <>
            <div className="flex h-14 items-center justify-between gap-1 px-3">
              {/* min-w-0 so `truncate` can actually shrink this flex item, and title so a
                  genuinely long studio name is still readable on hover when it does. */}
              <Link
                to="/"
                title={headerName}
                className="min-w-0 text-base font-semibold tracking-tight hover:text-muted-foreground transition-colors truncate"
              >
                {headerName}
              </Link>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost" size="icon"
                  className="h-8 w-8 shrink-0"
                  title={t('layout:sidebar.collapse')}
                  onClick={() => {
                    const next = true
                    setSidebarCollapsed(next)
                    localStorage.setItem('sf_sidebar_collapsed', String(next))
                    qc.getQueryData(queryKeys.settings.preferences) &&
                      apiClient.put('/team-members/me/preferences', { sidebarState: 'collapsed' }).catch(() => {})
                  }}
                >
                  <PanelLeftClose size={16} />
                </Button>
                {newTaskSidebarButton}
                <ThemeToggle />
              </div>
            </div>
            <Separator />
            <NavLinks visibleItems={navItems} />
            <Separator />
            {isOwnerOrAdmin && storageData && storageData.usagePercent >= 50 && (
              <div className="px-3 pt-2">
                <StorageIndicator storage={storageData} variant="bar" />
              </div>
            )}
            <div className="p-3 flex items-center justify-between">
              <div className="flex-1 min-w-0">{userMenu}</div>
              {desktopBell}
            </div>
          </>
        )}
      </aside>

      {/* ── Main area ── */}
      {/* Below md there is no top bar any more (RB-40 D84): the column itself steps under the
          status bar, and the phone bar at its foot owns the home-indicator inset. */}
      <div className="flex flex-1 flex-col overflow-hidden max-md:pt-[env(safe-area-inset-top,0px)]">

        {/* PWA onboarding banner */}
        <Suspense fallback={null}>
          <PWAOnboardingBanner />
        </Suspense>

        <main
          ref={mainScrollRef}
          id="main-content"
          // -1: focusable by the skip link, but not a tab stop of its own.
          tabIndex={-1}
          className="focus:outline-none flex flex-1 flex-col overflow-auto overflow-x-hidden bg-surface-page [scrollbar-gutter:stable] md:pb-[env(safe-area-inset-bottom,0px)]"
        >
          {/* Lifecycle banner — TRIAL_GRACE / CANCELLED_GRACE / READ_ONLY /
              PENDING_DELETION. Inside <main> so it scrolls with content. */}
          <Suspense fallback={null}>
            <LifecycleBanner />
          </Suspense>
          {/* Every inner route is lazy-loaded (see router/index.tsx). The
              Suspense boundary sits here so the sidebar + header remain
              painted while the active page chunk downloads. */}
          <Suspense fallback={
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              {t('layout:suspense.loading')}
            </div>
          }>
            <Outlet />
          </Suspense>
        </main>

        {/* Global upload progress — persists across navigation */}
        <Suspense fallback={null}>
          <GlobalUploadProgress />
        </Suspense>

        {taskCreateOpen && (
          <Suspense fallback={null}>
            <GlobalTaskCreateDialog
              open={taskCreateOpen}
              onOpenChange={setTaskCreateOpen}
              isOwnerOrAdmin={isOwnerOrAdmin}
            />
          </Suspense>
        )}

        {/* The phone bar (RB-40 D84): Dashboard · Notifications · + · Chat · Menu. It replaced the
            top bar and both floating buttons; below md only. */}
        <PhoneBar
          ref={menuButtonRef}
          unreadNotifications={unreadCount}
          chatUnread={totalUnread}
          onChat={isOnChatPage}
          onOpenChat={() => { setSheetOpen(false); setPhoneNotifOpen(false); setCreateSheetOpen(false); handleChatClick() }}
          notificationsOpen={phoneNotifOpen}
          // The two sheets sit above the bar and leave it live (IN-322), so each bar control
          // toggles its own sheet and closes the other two layers.
          onNotifications={() => { setSheetOpen(false); setCreateSheetOpen(false); setPhoneNotifOpen(v => !v) }}
          onCreate={() => { setSheetOpen(false); setPhoneNotifOpen(false); setCreateSheetOpen(v => !v) }}
          createLocked={writesLocked}
          menuOpen={sheetOpen}
          onMenu={() => { setPhoneNotifOpen(false); setCreateSheetOpen(false); setSheetOpen(v => !v) }}
        />
        <FullScreenMenu
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={t('layout:mobileNav.title')}
          returnFocusTo={menuButtonRef}
          bottomOffset={PHONE_BAR_OFFSET}
          header={<>
            {/* What the top bar carried and the bar has no room for: the studio, its storage,
                the theme and the account. */}
            <Link
              to="/"
              className="flex min-h-11 min-w-0 items-center font-semibold tracking-tight active:bg-accent [-webkit-tap-highlight-color:transparent]"
              onClick={e => navigateFromDrawer(e, '/')}
            >
              <span className="truncate">{headerName}</span>
            </Link>
            {isOwnerOrAdmin && <StorageIndicator storage={storageData} />}
            <span className="flex-1" />
            <ThemeToggle />
            <div className="min-w-0 max-w-[9rem]">{userMenu}</div>
          </>}
        >
          <NavLinks visibleItems={navItems} onNavigate={navigateFromDrawer} variant="grid" />
        </FullScreenMenu>
        <PhoneCreateSheet
          open={createSheetOpen}
          onOpenChange={setCreateSheetOpen}
          roles={userRoles}
          onNewTask={() => setTaskCreateOpen(true)}
          navigate={navigate}
        />
        <PhoneNotificationsSheet
          open={phoneNotifOpen}
          onOpenChange={setPhoneNotifOpen}
          unreadCount={unreadCount}
          isOwnerOrAdmin={isOwnerOrAdmin}
        />
      </div>
    </div>
  )
}

// ── Storage Indicator ─────────────────────────────────────────────────────────

function StorageIndicator({ storage, variant = 'pill' }: { storage: { usagePercent: number; usedBytes: number; limitBytes: number } | undefined; variant?: 'pill' | 'bar' }) {
  const { t } = useTranslation()
  if (!storage || storage.usagePercent < 50) return null

  const pct = storage.usagePercent
  const isFull = pct >= 100

  const barColor =
    isFull    ? 'bg-red-600' :
    pct >= 90 ? 'bg-red-500' :
    pct >= 75 ? 'bg-amber-500' :
                'bg-gray-400'

  const textColor =
    isFull    ? 'text-red-700' :
    pct >= 90 ? 'text-red-600' :
    pct >= 75 ? 'text-amber-600' :
                'text-gray-500'

  if (variant === 'bar') {
    return (
      <Link to="/settings" className="block hover:opacity-80 transition-opacity" title={t('layout:storage.manage')}>
        <div className="flex items-center justify-between mb-1">
          <span className={`text-[11px] font-medium ${textColor}`}>{t('layout:storage.label')}</span>
          <span className={`text-[11px] font-semibold ${textColor}`}>
            {isFull ? t('layout:storage.full') : `${Math.round(pct)}%`}
          </span>
        </div>
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      </Link>
    )
  }

  // Pill variant (for mobile header)
  const pillLabel = isFull ? t('layout:storage.full') : `${Math.round(pct)}%`
  const pillColor =
    isFull    ? 'bg-red-600 text-white border-red-600' :
    pct >= 90 ? 'bg-red-100 text-red-700 border-red-200' :
    pct >= 75 ? 'bg-amber-100 text-amber-700 border-amber-200' :
                'bg-gray-100 text-gray-600 border-gray-200'

  return (
    <Link
      to="/settings"
      className={`shrink-0 whitespace-nowrap inline-flex items-center px-1.5 py-0.5 text-[11px] font-semibold rounded-full border ${pillColor} hover:opacity-80 transition-opacity`}
      title={t('layout:storage.tooltip', { used: formatBytes(storage.usedBytes), limit: formatBytes(storage.limitBytes) })}
    >
      {pillLabel}
    </Link>
  )
}
